import { normalizeSearch } from "./search-normalization";
import type { ProductView } from "./types";

export type PriceRelation = "cheaper" | "dearer" | "same";
export type ComparisonBasis = "unit" | "item";

export interface ProductAnalogue {
  product: ProductView;
  relation: PriceRelation;
  differencePercent: number;
  basis: ComparisonBasis;
  exactProduct: boolean;
}

export interface ProductRelationshipIndex {
  byKey: ReadonlyMap<string, ProductView>;
  byCategory: ReadonlyMap<string, readonly ProductView[]>;
  byComparisonGroup: ReadonlyMap<string, readonly ProductView[]>;
  byProductId: ReadonlyMap<string, readonly ProductView[]>;
}

const SIMILARITY_STOP_WORDS = new Set([
  "and",
  "sau",
  "din",
  "pentru",
  "produs",
  "produse",
  "sortiment",
  "sortimente",
  "divers",
  "diverse",
  "variat",
  "variate",
  "pret",
  "bucata",
  "ambalat",
  "ambalata",
  "refrigerat",
  "refrigerata",
  "proaspat",
  "proaspata",
  "grasime",
  "calitatea",
  "или",
  "для",
  "из",
  "со",
  "в",
  "на",
  "по",
  "ассортименте",
  "ассортимент",
  "разные",
  "различные",
  "виды",
  "варианты",
  "товар",
  "цена",
  "штуку",
  "упаковке",
  "охлажденный",
  "охлажденная",
  "свежий",
  "свежая",
  "жирности",
  "первого",
  "сорта",
]);

// Strict comparison groups remain deliberately narrow. These clusters are
// used only to rank the looser "similar products" section, where related
// substitutes should appear before the rest of the broad category.
const SIMILARITY_GROUP_CLUSTERS: readonly (readonly string[])[] = [
  ["dairy_butter", "dairy_ghee", "dairy_spread"],
  ["dairy_milk", "dairy_kefir", "dairy_yogurt", "dairy_drinking_yogurt"],
  [
    "dairy_sour_cream",
    "dairy_cream",
    "dairy_cooking_cream",
    "dairy_coffee_cream",
  ],
  [
    "dairy_glazed_curd",
    "dairy_curd_snack",
    "dairy_cottage_cheese",
    "dairy_mascarpone",
  ],
  [
    "dairy_mozzarella",
    "dairy_parmesan",
    "dairy_brie_camembert",
    "dairy_blue_cheese",
    "dairy_cream_cheese",
    "dairy_processed_cheese",
    "dairy_brined_cheese",
    "dairy_hard_cheese",
  ],
  [
    "meat_chicken_breast",
    "meat_chicken_breast_prepared",
    "meat_chicken_breast_smoked",
    "meat_chicken_thighs",
    "meat_chicken_thighs_prepared",
    "meat_chicken_drumsticks",
    "meat_chicken_drumsticks_prepared",
    "meat_chicken_wings",
    "meat_chicken_wings_prepared",
    "meat_whole_chicken",
  ],
  [
    "meat_pork_neck",
    "meat_pork_neck_smoked",
    "meat_pork_ribs",
    "meat_pork_pulp",
    "meat_minced",
    "meat_minced_chicken",
    "meat_minced_pork",
    "meat_minced_beef",
    "meat_minced_mixed",
  ],
  [
    "meat_frankfurters",
    "meat_sausages",
    "meat_sausage_snacks",
    "meat_salame",
    "meat_salami_cooked",
    "meat_salami_cooked_smoked",
    "meat_salami_semi_smoked",
    "meat_salami_smoked",
    "meat_salami_dry_cured",
    "meat_ham",
    "meat_ham_cured",
    "meat_bacon_kaiser",
  ],
  ["fish_salmon_roe", "fish_capelin_roe", "fish_herring_roe"],
  [
    "fish_salmon_fresh",
    "fish_salmon_frozen",
    "fish_salmon_preserved",
    "fish_salmon_parts",
    "fish_trout",
  ],
  [
    "fish_mackerel_fresh",
    "fish_mackerel_frozen",
    "fish_mackerel_smoked",
    "fish_mackerel_canned",
    "fish_herring_preserved",
    "fish_herring_smoked",
    "fish_herring_frozen",
    "fish_sardines_canned",
    "fish_tuna_canned",
    "fish_tuna_pate",
  ],
  ["pantry_sunflower_oil", "pantry_olive_oil"],
  ["pantry_pasta", "pantry_rice", "pantry_buckwheat"],
  ["pantry_flour", "pantry_wheat_flour", "pantry_corn_flour"],
  ["pantry_sugar", "pantry_coconut_sugar"],
  [
    "pantry_mayonnaise",
    "pantry_ketchup",
    "pantry_mustard",
    "pantry_tomato_paste",
  ],
  ["pantry_canned_corn", "pantry_canned_peas", "pantry_canned_beans"],
  ["pantry_instant_coffee", "pantry_ground_coffee", "pantry_coffee_beans"],
  ["frozen_dumplings", "frozen_vareniki"],
  ["frozen_pizza", "frozen_pizza_base"],
  ["bakery_bread", "bakery_baguette"],
  ["sweets_chocolate", "sweets_candy", "sweets_cookies", "sweets_wafers"],
  [
    "snacks_nuts",
    "snacks_peanuts",
    "snacks_cashews",
    "snacks_almonds",
    "snacks_pistachios",
    "snacks_hazelnuts",
    "snacks_walnuts",
    "snacks_macadamia",
    "snacks_pecans",
    "snacks_nut_mix",
    "snacks_nut_fruit_mix",
    "snacks_nut_spread",
    "sweets_halva",
  ],
  ["snacks_chips", "snacks_crackers", "snacks_seeds"],
  [
    "drinks_water",
    "drinks_water_still",
    "drinks_water_sparkling",
    "drinks_flavored_water",
    "drinks_soda",
    "drinks_juice_nectar",
    "drinks_iced_tea",
  ],
  [
    "household_laundry_liquid",
    "household_laundry_powder",
    "household_laundry_capsules",
    "household_fabric_softener",
  ],
  [
    "household_toilet_cleaner",
    "household_glass_cleaner",
    "household_floor_cleaner",
    "household_bleach",
    "household_multipurpose_cleaner",
  ],
  [
    "beauty_shampoo",
    "beauty_dry_shampoo",
    "beauty_shampoo_set",
    "beauty_conditioner",
  ],
  ["beauty_shower_gel", "beauty_liquid_soap", "beauty_body_lotion"],
  ["beauty_toothpaste", "beauty_mouthwash"],
  [
    "pets_cat_food_dry",
    "pets_cat_food_dry_sterilized",
    "pets_cat_food_dry_kitten",
    "pets_cat_food_wet",
    "pets_cat_treats",
  ],
  [
    "pets_dog_food_dry",
    "pets_dog_food_dry_adult",
    "pets_dog_food_dry_puppy",
    "pets_dog_food_wet",
    "pets_dog_treats",
  ],
  ["alcohol_sparkling_wine", "alcohol_wine"],
  ["alcohol_vodka", "alcohol_whisky", "alcohol_cognac", "alcohol_rum", "alcohol_gin"],
];

const SIMILARITY_GROUP_CLUSTER = new Map<string, number>();
for (const [clusterIndex, groups] of SIMILARITY_GROUP_CLUSTERS.entries()) {
  for (const group of groups) {
    SIMILARITY_GROUP_CLUSTER.set(group, clusterIndex);
  }
}

const CATEGORIES_WITHOUT_GENERIC_FALLBACK = new Set([
  "home_nonfood",
  "other",
]);

export function relatedProductKey(product: ProductView): string {
  return `${product.catalog_id}:${product.id}`;
}

function pushGrouped(
  groups: Map<string, ProductView[]>,
  key: string | undefined,
  product: ProductView,
): void {
  if (!key) {
    return;
  }
  const values = groups.get(key);
  if (values) {
    values.push(product);
  } else {
    groups.set(key, [product]);
  }
}

export function buildProductRelationshipIndex(
  products: readonly ProductView[],
): ProductRelationshipIndex {
  const byKey = new Map<string, ProductView>();
  const byCategory = new Map<string, ProductView[]>();
  const byComparisonGroup = new Map<string, ProductView[]>();
  const byProductId = new Map<string, ProductView[]>();
  for (const product of products) {
    byKey.set(relatedProductKey(product), product);
    pushGrouped(byCategory, product.category_id, product);
    pushGrouped(byComparisonGroup, product.comparison_group_id, product);
    pushGrouped(byProductId, product.product_id, product);
  }
  return { byKey, byCategory, byComparisonGroup, byProductId };
}

function exactUnitValue(product: ProductView): number | null {
  const unit = product.unit_price;
  if (
    !unit ||
    unit.is_range ||
    !Number.isFinite(unit.minimum_mdl) ||
    unit.minimum_mdl <= 0
  ) {
    return null;
  }
  return unit.minimum_mdl;
}

function itemPrice(product: ProductView): number | null {
  const value = product.new_price_mdl;
  return value !== null && Number.isFinite(value) && value > 0 ? value : null;
}

function comparisonValues(
  current: ProductView,
  candidate: ProductView,
  exactProduct: boolean,
): { current: number; candidate: number; basis: ComparisonBasis } | null {
  const currentUnit = exactUnitValue(current);
  const candidateUnit = exactUnitValue(candidate);
  if (
    current.comparison_group_id &&
    current.comparison_group_id === candidate.comparison_group_id &&
    currentUnit !== null &&
    candidateUnit !== null &&
    current.unit_price?.basis === candidate.unit_price?.basis
  ) {
    return { current: currentUnit, candidate: candidateUnit, basis: "unit" };
  }
  if (exactProduct) {
    const currentItem = itemPrice(current);
    const candidateItem = itemPrice(candidate);
    if (currentItem !== null && candidateItem !== null) {
      return { current: currentItem, candidate: candidateItem, basis: "item" };
    }
  }
  return null;
}

function roundedDifference(value: number): number {
  return Math.round(Math.abs(value) * 10) / 10;
}

function relationFor(difference: number): PriceRelation {
  if (Math.abs(difference) < 0.05) {
    return "same";
  }
  return difference < 0 ? "cheaper" : "dearer";
}

function offerPreference(left: ProductAnalogue, right: ProductAnalogue): number {
  const relationRank = (value: PriceRelation): number =>
    value === "cheaper" ? 0 : value === "same" ? 1 : 2;
  const differenceOrder =
    left.relation === "cheaper"
      ? right.differencePercent - left.differencePercent
      : left.differencePercent - right.differencePercent;
  return (
    relationRank(left.relation) - relationRank(right.relation) ||
    differenceOrder ||
    (exactUnitValue(left.product) ?? Number.POSITIVE_INFINITY) -
      (exactUnitValue(right.product) ?? Number.POSITIVE_INFINITY) ||
    (itemPrice(left.product) ?? Number.POSITIVE_INFINITY) -
      (itemPrice(right.product) ?? Number.POSITIVE_INFINITY) ||
    left.product.store.localeCompare(right.product.store)
  );
}

export function productAnalogues(
  current: ProductView,
  index: ProductRelationshipIndex,
): ProductAnalogue[] {
  const candidates = new Map<string, ProductView>();
  if (current.product_id) {
    for (const candidate of index.byProductId.get(current.product_id) ?? []) {
      candidates.set(relatedProductKey(candidate), candidate);
    }
  }
  if (current.comparison_group_id) {
    for (const candidate of
      index.byComparisonGroup.get(current.comparison_group_id) ?? []) {
      candidates.set(relatedProductKey(candidate), candidate);
    }
  }
  candidates.delete(relatedProductKey(current));

  const analogues: ProductAnalogue[] = [];
  for (const candidate of candidates.values()) {
    const exactProduct = Boolean(
      current.product_id && current.product_id === candidate.product_id,
    );
    const values = comparisonValues(current, candidate, exactProduct);
    if (!values) {
      continue;
    }
    const rawDifference =
      ((values.candidate - values.current) / values.current) * 100;
    analogues.push({
      product: candidate,
      relation: relationFor(rawDifference),
      differencePercent: roundedDifference(rawDifference),
      basis: values.basis,
      exactProduct,
    });
  }

  // An overlapping catalog can contain the same SKU in the same store. Keep
  // only its most useful active offer while preserving cross-store matches.
  const deduplicated = new Map<string, ProductAnalogue>();
  for (const analogue of analogues.sort(offerPreference)) {
    const key = analogue.product.product_id
      ? `${analogue.product.product_id}\u0000${analogue.product.store}`
      : relatedProductKey(analogue.product);
    if (!deduplicated.has(key)) {
      deduplicated.set(key, analogue);
    }
  }
  return Array.from(deduplicated.values()).sort(offerPreference);
}

function tokenSet(product: ProductView): Set<string> {
  const brandTokens = new Set(normalizeSearch(product.brand).split(" "));
  const values = `${product.product_ru} ${product.product_ro}`;
  return new Set(
    normalizeSearch(values)
      .split(" ")
      .filter(
        (token) =>
          token.length >= 3 &&
          !/^\d+(?:[.,]\d+)?$/u.test(token) &&
          !SIMILARITY_STOP_WORDS.has(token) &&
          !brandTokens.has(token),
      ),
  );
}

function overlapScore(currentTokens: Set<string>, candidate: ProductView): number {
  const candidateTokens = tokenSet(candidate);
  let score = 0;
  for (const token of candidateTokens) {
    if (currentTokens.has(token)) {
      score += Math.min(9, token.length);
      continue;
    }
    for (const currentToken of currentTokens) {
      const maximum = Math.min(token.length, currentToken.length);
      let prefixLength = 0;
      while (
        prefixLength < maximum &&
        token[prefixLength] === currentToken[prefixLength]
      ) {
        prefixLength += 1;
      }
      if (prefixLength >= 5) {
        score += Math.min(7, prefixLength);
        break;
      }
    }
  }
  return score;
}

interface SimilarCandidate {
  product: ProductView;
  sameSemanticCluster: boolean;
  tokenScore: number;
  sameBasis: boolean;
  discount: number;
}

export function similarProducts(
  current: ProductView,
  index: ProductRelationshipIndex,
  excludedKeys: ReadonlySet<string>,
  limit = 12,
): ProductView[] {
  const categoryId = current.category_id;
  if (!categoryId || categoryId === "other" || limit <= 0) {
    return [];
  }
  const currentTokens = tokenSet(current);
  const currentSemanticCluster = current.comparison_group_id
    ? SIMILARITY_GROUP_CLUSTER.get(current.comparison_group_id)
    : undefined;
  const genericFallbackAllowed = !CATEGORIES_WITHOUT_GENERIC_FALLBACK.has(
    categoryId,
  );
  const candidates: SimilarCandidate[] = [];
  for (const candidate of index.byCategory.get(categoryId) ?? []) {
    const key = relatedProductKey(candidate);
    if (key === relatedProductKey(current) || excludedKeys.has(key)) {
      continue;
    }
    if (current.product_id && current.product_id === candidate.product_id) {
      continue;
    }
    const tokenScore = overlapScore(currentTokens, candidate);
    if (!genericFallbackAllowed && tokenScore === 0) {
      continue;
    }
    candidates.push({
      product: candidate,
      sameSemanticCluster: Boolean(
        currentSemanticCluster !== undefined &&
          candidate.comparison_group_id &&
          SIMILARITY_GROUP_CLUSTER.get(candidate.comparison_group_id) ===
            currentSemanticCluster,
      ),
      tokenScore,
      sameBasis: Boolean(
        current.unit_price &&
          candidate.unit_price &&
          current.unit_price.basis === candidate.unit_price.basis,
      ),
      discount: candidate.discount_percent ?? -1,
    });
  }
  candidates.sort(
    (left, right) =>
      Number(right.sameSemanticCluster) - Number(left.sameSemanticCluster) ||
      right.tokenScore - left.tokenScore ||
      Number(right.sameBasis) - Number(left.sameBasis) ||
      right.discount - left.discount ||
      (itemPrice(left.product) ?? Number.POSITIVE_INFINITY) -
        (itemPrice(right.product) ?? Number.POSITIVE_INFINITY) ||
      left.product.store.localeCompare(right.product.store),
  );
  return candidates.slice(0, limit).map((candidate) => candidate.product);
}
